import { FC } from 'react';
import { Alert, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import ConfirmModal from '../../../modals/confirm-modal';
import { IParentChange, IParentChangeAction, IParentChangePreview } from '../../../../interfaces/fileimport';
import styles from './styles';

export interface IParentChangesModalProps {
  open      : boolean;
  filename? : string;
  preview   : IParentChangePreview | null;
  onConfirm : () => void;
  onCancel  : () => void;
}

const actionLabels: Partial<Record<IParentChangeAction, string>> = {
  ADDED   : 'Added',
  CHANGED : 'Changed',
  CLEARED : 'Cleared',
  CONFLICT: 'Manual parent kept',
};

/**
 * This function checks if the preview has anything the user should review before the import commits.
 * @param preview The parent change preview of a staged document.
 * @returns True if any parent is added, changed or cleared, or a conflict or parent warning exists.
 */
export const hasParentChanges = (preview: IParentChangePreview | null | undefined): boolean => {
  if (!preview) return false;
  return (preview.customerChanges?.length ?? 0) > 0 || (preview.parentChanges?.length ?? 0) > 0;
};

/**
 * This function builds the warning message of a parent that loses customers or is removed.
 * @param parent The parent change row.
 * @returns The warning message.
 */
const getParentMessage = (parent: IParentChange) => {
  const name = parent.custName ?? parent.custSrcId;
  if (parent.action === 'REMOVED') {
    return `${name} will lose all its customers and will be removed.`;
  }

  const vendorLinks = `${parent.vendorCount} vendor link${parent.vendorCount === 1 ? '' : 's'}`;
  return parent.remainingCustomers
    ? `${name} has ${vendorLinks} and will lose some of its customers.`
    : `${name} has ${vendorLinks} and will lose all its customers. It will be kept with its vendor links.`;
};

/**
 * Modal that lists the parent changes of an AR Aging or Customer List import before it commits.
 * @param props The props for the parent changes modal.
 * @returns A modal to review the parent changes and continue or cancel the import.
 */
const ParentChangesModal: FC<IParentChangesModalProps> = ({ open, filename, preview, onConfirm, onCancel }) => {
  if (!preview) return null;

  const conflictCustomerCount = preview.customerChanges.filter(change => change.action === 'CONFLICT').length;

  // Counts are of distinct parents, several customers of the same parent count once
  const counts = [
    { label: 'Parents added',       value: preview.addedCount },
    { label: 'Parents changed',     value: preview.changedCount },
    { label: 'Parents cleared',     value: preview.clearedCount },
    { label: 'Manual parents kept', value: preview.conflictCount },
    { label: 'Parents removed',     value: preview.removedParentCount },
  ];

  return (
    <ConfirmModal
      open={open}
      onClose={onCancel}
      onConfirm={onConfirm}
      title='Review Parent Changes'
      description={`Importing ${filename ?? 'this file'} will make the following parent changes.`}
      yesButtonText='Continue Import'
      noButtonText='Cancel Import'
      confirmOnly
      alignment='left'
      // Above the import loader, which is fixed at z-index 9999
      modalSx={{ zIndex: 10000 }}
    >
      <Box sx={styles.content} data-testid='parent-changes-modal'>
        <Box sx={styles.countsContainer}>
          {counts.map(count => (
            <Box key={count.label} sx={styles.count}>
              <Typography sx={styles.countValue}>{count.value}</Typography>
              <Typography sx={styles.countLabel}>{count.label}</Typography>
            </Box>
          ))}
        </Box>

        {preview.parentChanges.map(parent => (
          <Alert
            key={`${parent.arCollateralId}-${parent.custSrcId}`}
            severity={parent.vendorCount ? 'warning' : 'info'}
            sx={styles.alert}
          >
            {getParentMessage(parent)}
          </Alert>
        ))}

        {conflictCustomerCount > 0 &&
          <Alert severity='info' sx={styles.alert}>
            {`${conflictCustomerCount} customer${conflictCustomerCount === 1 ? ' belongs' : 's belong'} to a parent set manually. The parent in the file will not be applied.`}
          </Alert>
        }

        {preview.customerChanges.length > 0 &&
          <TableContainer sx={styles.tableContainer}>
            <Table size='small' stickyHeader aria-label='Parent changes'>
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.headerCell}>Customer</TableCell>
                  <TableCell sx={styles.headerCell}>Current Parent</TableCell>
                  <TableCell sx={styles.headerCell}>Parent in File</TableCell>
                  <TableCell sx={styles.headerCell}>Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.customerChanges.map(change => (
                  <TableRow key={`${change.arCollateralId}-${change.custSrcId}`}>
                    <TableCell sx={styles.cell}>{change.custName ?? change.custSrcId}</TableCell>
                    <TableCell sx={styles.cell}>{change.currentParent ?? '-'}</TableCell>
                    <TableCell sx={styles.cell}>{change.action === 'CLEARED' ? '-' : change.fileParent}</TableCell>
                    <TableCell sx={styles.cell}>{actionLabels[change.action] ?? change.action}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        }
      </Box>
    </ConfirmModal>
  );
};

export default ParentChangesModal;
